import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

/**
 * Classe utilitaire pour générer des PDF à partir de contenu HTML
 */
class PDFGenerator {
  static async _fetchLogoAsPngDataUrl(logoUrl) {
    if (!logoUrl) return null;
    try {
      const response = await fetch(logoUrl, { credentials: 'include', cache: 'no-store' });
      if (!response.ok) return null;

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
        img.src = objectUrl;
      });

      URL.revokeObjectURL(objectUrl);

      if (!img.naturalWidth || !img.naturalHeight) return null;

      // Réduire la taille de l'image pour alléger le PDF (max 400px de large)
      const maxSize = 400;
      let targetWidth = img.naturalWidth;
      let targetHeight = img.naturalHeight;
      
      if (targetWidth > maxSize || targetHeight > maxSize) {
        if (targetWidth > targetHeight) {
          targetHeight = Math.round((targetHeight / targetWidth) * maxSize);
          targetWidth = maxSize;
        } else {
          targetWidth = Math.round((targetWidth / targetHeight) * maxSize);
          targetHeight = maxSize;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      // Fond blanc pour éviter le noir sur les zones transparentes
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, targetWidth, targetHeight);
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      // Utiliser PNG pour garder la qualité, taille réduite grâce au redimensionnement
      return {
        dataUrl: canvas.toDataURL('image/png'),
        width: targetWidth,
        height: targetHeight,
      };
    } catch (e) {
      return null;
    }
  }

  /**
   * Génère un PDF à partir d'un élément HTML
   * @param {HTMLElement} element - L'élément HTML à convertir en PDF
   * @param {string} filename - Le nom du fichier PDF à générer
   * @param {Object} options - Options de génération
   * @param {boolean} options.protege - Si le PDF doit être protégé par mot de passe
   * @param {string} options.motDePasse - Mot de passe pour protéger le PDF
   * @param {boolean} options.compresser - Si le PDF doit être compressé
   * @returns {Promise<Blob>} - Le blob du PDF généré
   */
  static async genererDepuisHTML(element, filename, options = {}) {
    try {
      // Capture l'élément HTML en canvas
      const canvas = await html2canvas(element, {
        scale: 2, // Meilleure qualité
        useCORS: true, // Permet de charger des images cross-origin
        logging: false,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      
      // Dimensions du PDF (A4)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 210; // Largeur A4 en mm
      const pageHeight = 297; // Hauteur A4 en mm
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      // Première page
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Pages supplémentaires si nécessaire
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Protection du PDF si demandé
      if (options.protege && options.motDePasse) {
        pdf.setEncryption(options.motDePasse, options.motDePasse, {
          printing: 'highResolution',
          copying: false,
          modifying: false,
          annotating: false,
          fillingForms: false,
          contentAccessibility: true,
          documentAssembly: false
        });
      }
      
      // Compression du PDF si demandé
      if (options.compresser) {
        pdf.compress = true;
      }
      
      // Génération du PDF
      const pdfBlob = pdf.output('blob');
      
      return pdfBlob;
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      throw error;
    }
  }
  
  /**
   * Télécharge un PDF généré
   * @param {Blob} pdfBlob - Le blob du PDF à télécharger
   * @param {string} filename - Le nom du fichier PDF
   */
  static telechargerPDF(pdfBlob, filename) {
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  
  /**
   * Envoie un PDF par email (simulation)
   * @param {Blob} pdfBlob - Le blob du PDF à envoyer
   * @param {string} email - L'adresse email du destinataire
   * @param {string} sujet - Le sujet de l'email
   * @param {string} message - Le message de l'email
   * @returns {Promise<boolean>} - True si l'envoi a réussi
   */
  static async envoyerParEmail(pdfBlob, email, sujet, message) {
    // Dans une application réelle, cette fonction enverrait le PDF à un backend
    // qui se chargerait d'envoyer l'email avec le PDF en pièce jointe
    
    // Simulation d'un appel API
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log(`PDF envoyé à ${email} avec le sujet "${sujet}"`);
        resolve(true);
      }, 1500);
    });
  }
  
  /**
   * Programme l'envoi automatique d'un rapport (simulation)
   * @param {number} clientId - ID du client
   * @param {string} typeRapport - Type de rapport
   * @param {string} frequence - Fréquence d'envoi (hebdomadaire, mensuel)
   * @param {Object} options - Options de génération et d'envoi
   * @returns {Promise<boolean>} - True si la programmation a réussi
   */
  static async programmerEnvoiAutomatique(clientId, typeRapport, frequence, options = {}) {
    // Dans une application réelle, cette fonction enregistrerait la programmation
    // dans une base de données pour un traitement ultérieur par un job scheduler
    
    // Simulation d'un appel API
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log(`Envoi automatique programmé pour le client ${clientId}, rapport ${typeRapport}, fréquence ${frequence}`);
        resolve(true);
      }, 1000);
    });
  }
  
  /**
   * Génère un PDF à partir d'une liste de chaînes de caractères
   * @param {string[]} documentList - La liste de documents à inclure
   * @param {string} filename - Le nom du fichier PDF
   * @param {string} logoUrl - L'URL du logo à afficher
   * @param {string} clientName - Le nom du client (optionnel)
   */
  static async genererDepuisTexte(documentList, filename, logoUrl, clientName = null) {
    const pdf = new jsPDF({ compress: true });
    let logoBase64 = null;
    let logoLoaded = false;
    let imgRatio = 1; // Pour stocker le ratio de l'image
    
    // Ajout du logo - Convertir en base64 pour compatibilité avec jsPDF
    if (logoUrl) {
      try {
        const fetched = await PDFGenerator._fetchLogoAsPngDataUrl(logoUrl);
        if (fetched && fetched.dataUrl) {
          logoBase64 = fetched.dataUrl;
          logoLoaded = true;
          imgRatio = fetched.width / fetched.height;
        }
        
        // Ajouter le logo en en-tête si chargé avec succès
        if (logoLoaded && logoBase64) {
          // Calculer les dimensions en respectant les proportions
          const maxWidth = 50;
          const maxHeight = 30;
          
          let logoWidth, logoHeight;
          
          // Ajuster par la largeur
          logoWidth = maxWidth;
          logoHeight = maxWidth / imgRatio;
        
        // Si la hauteur dépasse, ajuster par la hauteur
        if (logoHeight > maxHeight) {
          logoHeight = maxHeight;
            logoWidth = maxHeight * imgRatio;
        }
        
        // Centrer le logo en haut
        const logoX = (pdf.internal.pageSize.getWidth() - logoWidth) / 2;
          pdf.addImage(logoBase64, 'PNG', logoX, 10, logoWidth, logoHeight);
        }
      } catch (error) {
        console.warn('Erreur lors du traitement du logo:', error);
        logoLoaded = false;
      }
    }
    
    // Position du titre (après le logo)
    let currentY = 45;
    
    // Ajout du titre
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Liste de documents pour votre demande de visa', pdf.internal.pageSize.getWidth() / 2, currentY, { align: 'center' });
    
    currentY += 10;
    
    // Ajout du nom du client si fourni
    if (clientName) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Client: ${clientName}`, pdf.internal.pageSize.getWidth() / 2, currentY, { align: 'center' });
      currentY += 8;
    }
    
    // Ligne de séparation
    pdf.setDrawColor(200, 200, 200);
    pdf.line(15, currentY, pdf.internal.pageSize.getWidth() - 15, currentY);
    currentY += 5;
    
    // Préparation des données pour autoTable
    const tableData = documentList.map(doc => {
      let style = {};
      let content = doc;
      
      if (doc.startsWith('===')) {
        style = { fontStyle: 'bold', textColor: [26, 86, 171], fontSize: 14 };
        content = doc.replace(/===/g, '').trim();
      } else if (doc.startsWith('---')) {
        style = { fontStyle: 'bold', textColor: [4, 120, 87], fontSize: 12 };
        content = doc.replace(/---/g, '').trim();
      } else if (doc.startsWith('•')) {
        content = `    • ${doc.replace('•', '').trim()}`;
      }
      
      return { content, style };
    });

    // Utilisation de autoTable pour un meilleur formatage
    autoTable(pdf, {
      startY: currentY,
      body: tableData.map(d => [d.content]),
      theme: 'plain',
      styles: {
        font: 'helvetica',
        fontSize: 10,
      },
      willDrawPage: () => {
        // Dessiner le filigrane AVANT le contenu du tableau (background)
        if (!logoLoaded || !logoBase64) return;
        try {
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();

          const maxWatermarkWidth = pageWidth * 0.78;
          const maxWatermarkHeight = pageHeight * 0.78;

          let watermarkWidth = maxWatermarkWidth;
          let watermarkHeight = maxWatermarkWidth / imgRatio;

          if (watermarkHeight > maxWatermarkHeight) {
            watermarkHeight = maxWatermarkHeight;
            watermarkWidth = maxWatermarkHeight * imgRatio;
          }

          const x = (pageWidth - watermarkWidth) / 2;
          const y = (pageHeight - watermarkHeight) / 2;

          if (typeof pdf.GState === 'function' && typeof pdf.setGState === 'function') {
            pdf.setGState(new pdf.GState({ opacity: 0.10 }));
          }
          pdf.addImage(logoBase64, 'PNG', x, y, watermarkWidth, watermarkHeight);
          if (typeof pdf.GState === 'function' && typeof pdf.setGState === 'function') {
            pdf.setGState(new pdf.GState({ opacity: 1 }));
          }
        } catch (error) {
          console.warn('Erreur lors de l\'ajout du filigrane:', error);
        }
      },
      didParseCell: (data) => {
        const rowData = tableData[data.row.index];
        if (rowData && rowData.style) {
          data.cell.styles.fontStyle = rowData.style.fontStyle || 'normal';
          data.cell.styles.textColor = rowData.style.textColor || [0, 0, 0];
          data.cell.styles.fontSize = rowData.style.fontSize || 10;
        }
      }
    });

    pdf.save(filename);
  }
}

export default PDFGenerator;
